import { useEffect } from "react";
import Link from "next/link";
import sal from "sal.js";
import { useTranslation } from "react-i18next";

import Category from "./Home-Sections/Category";
import ValueProps from "./Home-Sections/ValueProps";
import TrustBar from "./Home-Sections/TrustBar";
import TechMarquee from "./Home-Sections/TechMarquee";
import FeaturedCourses from "./Home-Sections/FeaturedCourses";
import TrendingCourses from "./Home-Sections/TrendingCourses";
import ReimagineCareer from "./Home-Sections/ReimagineCareer";
import AdvanceTab from "../AdvanceTab/AdvanceTab";
import InstructorsShowcase from "./Home-Sections/InstructorsShowcase";
import TutorRecruitment from "./Home-Sections/TutorRecruitment";
import HomeFAQ from "./Home-Sections/HomeFAQ";
import OutcomesBand from "./Home-Sections/OutcomesBand";
import EventCarouse from "@/my_components/Events/EventCarouse";
import CallToActionOne from "@/my_components/CallToAction/CallToActionOne";
import BlogWidget from "@/my_components/Blogs/Widgets/BlogWidget";
import Newsletter from "@/my_components/Newsletters/Newsletter";

// HOME PAGE SECTIONS
import { Banner } from "@/my_components/_HomePage/Home-Sections/Banner";

// CONFIGS AND ROUTE
import { ROUTE } from "@/route/app_routes.js";
import { image_url } from "../../helper/Utilities";

const HomePageContents = () => {
  const { t } = useTranslation("home");

  useEffect(() => {
    sal({
      threshold: 0.01,
      once: true,
    });
  }, []);

  return (
    <>
      <Banner />

      <main className="rbt-main-wrapper">
        <TechMarquee />
        <TrustBar />

        <div className="rbt-section-gap2">
          <div className="container">
            <div className="rbt-sec-head">
              <div>
                <span className="rbt-eyebrow">
                  <span className="rbt-eyebrow-dot" aria-hidden="true" />
                  {t("featuredCourses.eyebrow", "Featured courses")}
                </span>
                <h2 className="rbt-sec-head__title">{t("featuredCourses.title", "Start learning today, for free")}</h2>
                <p className="rbt-sec-head__desc">
                  {t(
                    "featuredCourses.desc",
                    "A few of the free courses waiting for you — browse the full catalog to see everything on offer."
                  )}
                </p>
              </div>
              <Link className="rbt-btn rbt-switch-btn bg-primary-opacity btn-sm" href={ROUTE.course}>
                <span data-text={t("featuredCourses.browseAll", "Browse All")}>{t("featuredCourses.browseAll", "Browse All")}</span>
              </Link>
            </div>
            <FeaturedCourses />
          </div>
        </div>

        <div className="rbt-section-gap2">
          <div className="container">
            <div className="rbt-sec-head">
              <div>
                <span className="rbt-eyebrow">
                  <span className="rbt-eyebrow-dot" aria-hidden="true" />
                  {t("trendingCourses.eyebrow", "Right now")}
                </span>
                <h2 className="rbt-sec-head__title">{t("trendingCourses.title", "Trending courses")}</h2>
                <p className="rbt-sec-head__desc">
                  {t(
                    "trendingCourses.desc",
                    "Ranked by real enrollment activity — what learners are actually starting this month."
                  )}
                </p>
              </div>
            </div>
            <TrendingCourses />
          </div>
        </div>

        <TutorRecruitment />

        <div className="rbt-section-gap2">
          <div className="container">
            <div className="rbt-sec-head">
              <div>
                <span className="rbt-eyebrow">
                  <span className="rbt-eyebrow-dot" aria-hidden="true" />
                  {t("categories.eyebrow", "Explore by discipline")}
                </span>
                <h2 className="rbt-sec-head__title">{t("categories.title", "Popular categories")}</h2>
                <p className="rbt-sec-head__desc">
                  {t(
                    "categories.desc",
                    "Every track is real, project-based, and completely free — pick where you want to start."
                  )}
                </p>
              </div>
              <Link className="rbt-btn rbt-switch-btn bg-primary-opacity btn-sm" href={ROUTE.view_all_categories}>
                <span data-text={t("categories.viewAll", "View All")}>{t("categories.viewAll", "View All")}</span>
              </Link>
            </div>
            <Category />
          </div>
        </div>

        <div className="rbt-section-gap2 bg-color-white">
          <div className="container">
            <div className="rbt-sec-head" style={{ justifyContent: "center", textAlign: "center" }}>
              <div style={{ margin: "0 auto" }}>
                <span className="rbt-eyebrow">
                  <span className="rbt-eyebrow-dot" aria-hidden="true" />
                  {t("goals.eyebrow", "Explore by goal")}
                </span>
                <h2 className="rbt-sec-head__title">{t("goals.title", "What do you want to become?")}</h2>
                <p className="rbt-sec-head__desc" style={{ margin: "10px auto 0" }}>
                  {t("goals.desc", "Pick a path. Every course inside it is free, project-based, and stacks toward a real skill set.")}
                </p>
              </div>
            </div>
            <AdvanceTab />
          </div>
        </div>

        <ReimagineCareer />

        <div className="rbt-section-gap2">
          <div className="container">
            <div className="rbt-sec-head" style={{ justifyContent: "center", textAlign: "center" }}>
              <div style={{ margin: "0 auto" }}>
                <span className="rbt-eyebrow">
                  <span className="rbt-eyebrow-dot" aria-hidden="true" />
                  {t("instructors.eyebrow", "Who's teaching")}
                </span>
                <h2 className="rbt-sec-head__title">{t("instructors.title", "Meet your instructors")}</h2>
                <p className="rbt-sec-head__desc" style={{ margin: "10px auto 0" }}>
                  {t(
                    "instructors.desc",
                    "Real engineers and designers who've shipped production work, teaching the fundamentals they use every day."
                  )}
                </p>
              </div>
            </div>
            <InstructorsShowcase layout="carousel" limit={15} />

            <div className="text-center mt--40">
              <Link className="rbt-btn btn-gradient hover-icon-reverse" href={ROUTE.instructors}>
                <span className="icon-reverse-wrapper">
                  <span className="btn-text">{t("instructors.seeAll", "See all instructors")}</span>
                  <span className="btn-icon">
                    <i className="feather-arrow-right"></i>
                  </span>
                  <span className="btn-icon">
                    <i className="feather-arrow-right"></i>
                  </span>
                </span>
              </Link>
            </div>
          </div>
        </div>

        <div className="rbt-section-gap2">
          <div className="container">
            <div className="rbt-sec-head" style={{ justifyContent: "center", textAlign: "center" }}>
              <div style={{ margin: "0 auto" }}>
                <span className="rbt-eyebrow">
                  <span className="rbt-eyebrow-dot" aria-hidden="true" />
                  {t("valueProps.eyebrow", "Why learn here")}
                </span>
                <h2 className="rbt-sec-head__title">{t("valueProps.title", "Built for people who learn by doing")}</h2>
              </div>
            </div>
            <ValueProps />
          </div>
        </div>

        <div className="rbt-section-gap2 bg-color-white">
          <div className="container">
            <div className="rbt-sec-head" style={{ justifyContent: "center", textAlign: "center" }}>
              <div style={{ margin: "0 auto" }}>
                <span className="rbt-eyebrow">
                  <span className="rbt-eyebrow-dot" aria-hidden="true" />
                  {t("faq.eyebrow", "Frequently asked questions")}
                </span>
                <h2 className="rbt-sec-head__title">{t("faq.title", "Got questions? We've got answers")}</h2>
                <p className="rbt-sec-head__desc" style={{ margin: "10px auto 0" }}>
                  {t("faq.desc", "Everything you need to know before you start your first course.")}
                </p>
              </div>
            </div>
            <HomeFAQ />
          </div>
        </div>

        <OutcomesBand />

        <div className="rbt-events">
          <div className="container">
            <div className="rbt-sec-head">
              <div>
                <span className="rbt-eyebrow">
                  <span className="rbt-eyebrow-dot" aria-hidden="true" />
                  {t("events.eyebrow", "Interested in joining?")}
                </span>
                <h2 className="rbt-sec-head__title rbt-events__title">{t("events.title", "Upcoming events")}</h2>
                <p className="rbt-sec-head__desc rbt-events__desc">
                  {t("events.desc", "Live workshops and community sessions — free to attend, open to every skill level.")}
                </p>
              </div>
            </div>
            <EventCarouse />
          </div>
        </div>

        <div
          className="rbt-ready-area ready-section-01 rbt-section-gap bg_image rbt-cta-photo"
          style={{ backgroundImage: `url(${image_url("/images/home/cta-team.jpg")})` }}
        >
          <span className="rbt-cta-photo__scrim" aria-hidden="true" />
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="section-title text-center">
                  <span className="subtitle bg-primary-opacity"> {t("cta.subtitle", "Ready to start?")} </span>
                  <h2 className="title w-600">{t("cta.title", "Your tech career starts with lesson one")}</h2>
                  <p className="mt--10 description">
                    {" "}
                    {t("cta.desc", "Every course is free, self-paced, and built around real projects —")} <br />{" "}
                    {t("cta.descLine2", "pick one and start today.")}{" "}
                  </p>
                  <Link className="rbt-btn btn-gradient hover-icon-reverse" href={ROUTE.course}>
                    <span className="icon-reverse-wrapper">
                      <span className="btn-text">{t("cta.browseCourses", "Browse Courses")}</span>
                      <span className="btn-icon">
                        <i className="feather-arrow-right"></i>
                      </span>
                      <span className="btn-icon">
                        <i className="feather-arrow-right"></i>
                      </span>
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rbt-rbt-blog-area rbt-section-gap">
          <div className="container">
            <div className="row g-5 align-items-center mb--30">
              <div className="col-lg-6 col-md-6 col-12">
                <div className="section-title">
                  <span className="subtitle bg-primary-opacity">{t("blog.subtitle", "Blog Posts")}</span>
                  <h2 className="title">{t("blog.title", "Never miss what we are up to")}</h2>
                </div>
              </div>
              <div className="col-lg-6 col-md-6 col-12">
                <div className="read-more-btn text-start text-md-end">
                  <Link className="rbt-btn btn-gradient hover-icon-reverse" href={ROUTE.blog}>
                    <div className="icon-reverse-wrapper">
                      <span className="btn-text">{t("blog.seeAllArticles", "See All Articles")}</span>
                      <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                      <span className="btn-icon"><i className="feather-arrow-right"></i></span>
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            <BlogWidget extra_class="" />
          </div>
        </div>

        <CallToActionOne />

        <div
          className="rbt-newsletter-area bg-color-primary newsletter-style-2 rbt-section-gap"
          style={{ background: "url(" + image_url("/app_images/bg/dots.png") + ") repeat center/auto" }}
        >
          <Newsletter />
        </div>
      </main>
    </>
  );
};

export default HomePageContents;
